import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Form, message, Modal } from "antd";
import { useRequest } from "ahooks";
import { EditForm, FormConfigType } from "../../../components/editForm";
import { langCommon } from "../../../locales";
import { CardServer } from "../../../server";
import { generateOperationFormConfig, AREACODEMAP } from './config'

const server = CardServer.Holder;

const DEFAULT_OPERATION_DATA = {
    Id: null,
    Email: null,
    PhoneNumber: null,
    CountryCode: null,
}

type OperateModeType = 'update'

// 根据国家代码获取手机号长度规则
const getPhoneNumberRules = (countryCode: string | null | undefined) => {
    if (!countryCode || typeof countryCode !== 'string') {
        return { min: 8, max: 15 };
    }
    
    const code = countryCode.toUpperCase().trim();
    const areaInfo = AREACODEMAP[code as keyof typeof AREACODEMAP];
    
    if (areaInfo) {
        const min = areaInfo.length;
        const max = 'maxLength' in areaInfo ? areaInfo.maxLength : areaInfo.length;
        return { min, max };
    }
    
    // 如果没有找到对应的国家代码，使用默认值
    return { min: 8, max: 15 };
};

export const Operation = forwardRef((props: any, ref: any) => {
    const { onSuccess } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisiable] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>({ ...DEFAULT_OPERATION_DATA });
    const [form] = Form.useForm();
    const [mode, setMode] = useState<OperateModeType>('update');

    useImperativeHandle(ref, () => ({
        changeVisible
    }))

    const changeVisible = (val: any, mode?: OperateModeType) => {
        if (val) {
            const data = {
                ...DEFAULT_OPERATION_DATA,
                ...(mode === 'update' ? val : {})
            };
            // 如果数据中没有 CountryCode，尝试从 Country 字段获取
            if (!data.CountryCode && val.Country) {
                // 如果 Country 是完整国家名，可能需要映射，这里先直接使用
                // 实际项目中可能需要根据实际情况调整
                data.CountryCode = val.CountryCode || val.Country;
            }
            mode && setMode(mode)
            form?.resetFields?.();
            setFormData(data);
        } else if (val) {
            form?.resetFields?.();
        }
        setVisiable(!!val);
    }

    const { run } = useRequest(server.update, {
        manual: true,
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => changeVisible(false),
        onSuccess: (res: any) => {
            message.success(langCommon?.operateSuccess || '操作成功');
            changeVisible(false);
            onSuccess?.();
        }
    });

    const getBaseInfoFormConfig: FormConfigType = useMemo((): FormConfigType => {
        const phoneRules = getPhoneNumberRules(formData.CountryCode);
        return generateOperationFormConfig({ formData, mode, phoneRules });
    }, [formData, mode]);

    return (
        <div>
            <Modal
                title={'修改信息'}
                width={500}
                closeIcon={null}
                maskClosable={false}
                open={visible}
                onCancel={() => changeVisible(false)}
                okButtonProps={{ loading }}
                onOk={async () => {
                    await form.validateFields();
                    const { Id, Email, PhoneNumber } = formData
                    const params = {
                        Id,
                        Email: Email || "",
                        PhoneNumber: PhoneNumber || "",
                    }
                    run(params)
                }}
            >
                <EditForm
                    componentId="card-holder-operation"
                    className="mt20"
                    form={form}
                    formAttrsProps={{
                        labelCol: { span: 6 },
                        wrapperCol: { span: 18 },
                    }}
                    formData={formData}
                    onDataChange={(data: any) => {
                        setFormData(data);
                    }}
                    formConfig={getBaseInfoFormConfig}
                />
            </Modal>
        </div>
    );
})

